import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@/lib/types/auth';
import * as authService from '@/lib/services/auth';

type AuthContextValue = {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionStart: Date | null;
  tokenExpiresAt: Date | null;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionStart, setSessionStart] = useState<Date | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null);

  useEffect(() => {
    async function loadUser() {
      try {
        const currentUser = await authService.getMe();
        setUser(currentUser ?? null);
        setSessionStart(currentUser ? new Date() : null);
        if (currentUser) {
          const expiresAt = new Date();
          expiresAt.setMinutes(expiresAt.getMinutes() + 540);
          setTokenExpiresAt(expiresAt);
        } else {
          setTokenExpiresAt(null);
        }
      } catch {
        setUser(null);
        setSessionStart(null);
      } finally {
        setIsLoading(false);
      }
    }

    loadUser();
  }, []);

  async function handleRefreshSession() {
    try {
      await authService.refreshSession();
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 540);
      setTokenExpiresAt(expiresAt);
    } catch {
      setUser(null);
      setTokenExpiresAt(null);
      setSessionStart(null);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoading,
        isAuthenticated: user !== null,
        sessionStart,
        tokenExpiresAt,
        refreshSession: handleRefreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
